import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Scale, Plus, Trash2, ChevronLeft, AlertTriangle, CheckCircle2, Clock } from "lucide-react";

const STATUS = {
  pendente: { label: "Pendente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  recebido: { label: "Recebido", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  vencido: { label: "Vencido", cls: "bg-red-100 text-red-700 border-red-200" },
  nao_aplicavel: { label: "Não se aplica", cls: "bg-stone-100 text-stone-500 border-stone-200" },
};

const KIND_LABEL = { documento: "Documento", certidao: "Certidão", consulta: "Consulta" };

export default function LegalAdvisor() {
  const [dossiers, setDossiers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ operation: "venda", lead_id: "", property_ref: "" });

  const load = async () => {
    try {
      const [d, l] = await Promise.all([api.get("/legal/dossiers"), api.get("/leads")]);
      setDossiers(d.data); setLeads(l.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.lead_id) { toast.error("Escolhe o cliente da operação"); return; }
    try {
      const { data } = await api.post("/legal/dossiers", {
        operation: form.operation, lead_id: form.lead_id, property_ref: form.property_ref,
      });
      toast.success("Checklist criado");
      setNewOpen(false); setSelected(data); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar checklist"); }
  };

  const updateItem = async (itemId, patch) => {
    try {
      const { data } = await api.patch(`/legal/dossiers/${selected.id}/items/${itemId}`, patch);
      setSelected(data);
      setDossiers((ds) => ds.map((d) => (d.id === data.id ? data : d)));
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao atualizar item"); }
  };

  const removeDossier = async (id) => {
    if (!confirm("Excluir esse checklist?")) return;
    await api.delete(`/legal/dossiers/${id}`);
    setSelected(null); load();
    toast.success("Checklist excluído");
  };

  if (loading) return <div className="p-10 text-stone-400">Carregando...</div>;

  if (selected) {
    const parties = [...new Set(selected.items.map((i) => i.party))];
    return (
      <div className="p-5 lg:p-10 max-w-4xl mx-auto space-y-5" data-testid="legal-dossier-detail">
        <button onClick={() => setSelected(null)} className="text-sm text-stone-600 flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Voltar pros checklists
        </button>
        <Card className="p-5 rounded-2xl">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="font-heading text-2xl font-bold text-[#1B3A2F]">
                {selected.lead_name} · {selected.operation === "venda" ? "Venda" : "Locação"}
              </h1>
              {selected.property_ref && <div className="text-sm text-stone-500">Imóvel: {selected.property_ref}</div>}
            </div>
            <div className="text-right">
              <div className="font-heading text-3xl font-extrabold text-[#D4651A]" data-testid="legal-progress">
                {selected.summary.progress}%
              </div>
              <div className="text-xs text-stone-500">
                {selected.summary.required_ok}/{selected.summary.required_total} obrigatórios
              </div>
            </div>
          </div>
          {selected.summary.blockers.length > 0 && (
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm" data-testid="legal-blockers">
              <div className="font-semibold text-amber-900 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" /> Falta pra fechar com segurança
              </div>
              <ul className="mt-1 list-disc list-inside text-amber-900/90 text-xs space-y-0.5">
                {selected.summary.blockers.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </div>
          )}
          {selected.summary.ready && (
            <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Documentação obrigatória completa.
            </div>
          )}
        </Card>

        {parties.map((party) => (
          <Card key={party} className="p-5 rounded-2xl" data-testid={`legal-party-${party}`}>
            <h2 className="font-heading font-bold text-[#1B3A2F] mb-3">{selected.party_labels[party] || party}</h2>
            <div className="space-y-2">
              {selected.items.filter((i) => i.party === party).map((item) => {
                const st = STATUS[item.effective_status] || STATUS.pendente;
                return (
                  <div key={item.id} data-testid={`legal-item-${item.id}`}
                    className="rounded-lg border border-stone-200 p-3 flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-stone-800">
                        {item.title}
                        {item.required && <span className="text-red-500 ml-1" title="Obrigatório">*</span>}
                      </div>
                      <div className="text-xs text-stone-400 flex items-center gap-2 mt-0.5">
                        {KIND_LABEL[item.kind] || item.kind}
                        {item.expires_at && (
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> validade {item.expires_at}</span>
                        )}
                      </div>
                    </div>
                    <Badge className={`border ${st.cls} flex-shrink-0`}>{st.label}</Badge>
                    <div className="flex gap-2 flex-shrink-0">
                      <Select value={item.status} onValueChange={(v) => updateItem(item.id, { status: v })}>
                        <SelectTrigger className="w-[150px] h-8 text-xs" data-testid={`legal-item-status-${item.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input type="date" value={item.expires_at || ""} className="w-[135px] h-8 text-xs"
                        data-testid={`legal-item-expires-${item.id}`}
                        onChange={(e) => updateItem(item.id, { expires_at: e.target.value })} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
        <Button variant="outline" onClick={() => removeDossier(selected.id)} className="border-red-300 text-red-600">
          <Trash2 className="w-4 h-4 mr-1.5" /> Excluir checklist
        </Button>
      </div>
    );
  }

  return (
    <div className="p-5 lg:p-10 max-w-5xl mx-auto space-y-6" data-testid="legal-advisor-root">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4651A] font-bold mb-1">Segurança da operação</div>
          <h1 className="font-heading text-3xl font-bold text-[#1B3A2F] flex items-center gap-2">
            <Scale className="w-7 h-7" /> Assessor Jurídico
          </h1>
          <p className="text-stone-500 text-sm mt-1 max-w-xl">
            Checklist documental por operação: o que pedir, o que já chegou, o que está pendente e o que venceu.
          </p>
        </div>
        <Button onClick={() => setNewOpen(true)} data-testid="legal-new-dossier-btn"
          className="bg-[#D4651A] hover:bg-[#B85512] text-white gap-2">
          <Plus className="w-4 h-4" /> Novo checklist
        </Button>
      </header>

      {dossiers.length === 0 ? (
        <Card className="p-8 text-center text-stone-400" data-testid="legal-empty">
          Nenhum checklist ainda. Cria um pra venda ou locação e eu monto a lista de documentos e certidões.
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {dossiers.map((d) => (
            <Card key={d.id} data-testid={`legal-dossier-card-${d.id}`}
              onClick={() => setSelected(d)}
              className="p-4 rounded-2xl cursor-pointer hover:shadow-md transition">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-[#1B3A2F]">{d.lead_name}</div>
                  <div className="text-xs text-stone-500">
                    {d.operation === "venda" ? "Venda" : "Locação"}{d.property_ref ? ` · ${d.property_ref}` : ""}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-heading text-xl font-bold text-[#D4651A]">{d.summary.progress}%</div>
                  <div className="text-[11px] text-stone-400">{d.summary.counts.pendente} pendentes</div>
                </div>
              </div>
              {d.summary.counts.vencido > 0 && (
                <Badge className="mt-2 bg-red-100 text-red-700 border border-red-200">
                  {d.summary.counts.vencido} vencido(s)
                </Badge>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="legal-new-dossier-dialog">
          <DialogHeader><DialogTitle>Novo checklist documental</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Operação</Label>
              <Select value={form.operation} onValueChange={(v) => setForm({ ...form, operation: v })}>
                <SelectTrigger data-testid="legal-operation-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="venda">Venda</SelectItem>
                  <SelectItem value="locacao">Locação</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Cliente</Label>
              <Select value={form.lead_id} onValueChange={(v) => setForm({ ...form, lead_id: v })}>
                <SelectTrigger data-testid="legal-lead-select"><SelectValue placeholder="Escolhe o lead" /></SelectTrigger>
                <SelectContent>
                  {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Imóvel (código ou endereço)</Label>
              <Input data-testid="legal-property-input" value={form.property_ref}
                onChange={(e) => setForm({ ...form, property_ref: e.target.value })}
                placeholder="Ex.: AP0264 · Rua das Flores 200" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Cancelar</Button>
            <Button data-testid="legal-create-btn" onClick={create} className="bg-[#1B3A2F] hover:bg-[#244A3C] text-white">
              Criar checklist
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
